import { AGENDA_TEXTO_CITA } from "../../constants/agendaColores";
import {
  etiquetaTipoAgenda,
  linea1BloqueCita,
  linea2BloqueCita,
  tooltipBloqueCita,
} from "../../utils/agendaCitaDisplay";

const HANDLE_TOP = 16;
const HANDLE_BOTTOM = 10;
const COLOR_ASA_CITA = "rgba(0,0,0,0.35)";

function GripBar({ color }) {
  return (
    <div
      aria-hidden
      style={{
        display: "flex",
        gap: 3,
        pointerEvents: "none",
        flexShrink: 0,
      }}
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 12,
            height: 3,
            borderRadius: 2,
            background: color,
          }}
        />
      ))}
    </div>
  );
}

/** Bloque de cita en rejilla semana/día: arrastrar arriba, redimensionar abajo. */
export default function BloqueCitaCalendario({
  cita,
  topPx,
  altoPx,
  estilo,
  caja,
  esActiva,
  overlapColumn = 0,
  overlapColumnCount = 1,
  onMoverStart,
  onResizeStart,
  onAbrir,
  huboMovimientoRef,
  horaStr,
  nombreContacto,
  telefonoContacto,
}) {
  const ink = AGENDA_TEXTO_CITA;
  const puedeResize = altoPx >= HANDLE_TOP + HANDLE_BOTTOM + 16;
  const bloqueBajo = altoPx < 44;
  const horaIni = horaStr(cita.fecha_inicio);
  const horaFin = cita.fecha_fin ? horaStr(cita.fecha_fin) : "";
  const hora = horaFin ? `${horaIni}–${horaFin}` : horaIni;
  const nombre = nombreContacto(cita) || cita.titulo || "";
  const telefono = telefonoContacto?.(cita) || "";
  const linea1 = linea1BloqueCita(cita, { hora, nombre, telefono });
  const linea2 = linea2BloqueCita(cita);
  const tipoLabel = cita.tipo_etiqueta || etiquetaTipoAgenda(cita.tipo);
  const tip = tooltipBloqueCita(cita, { hora, nombre, telefono });

  const lineStyle = {
    color: ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const cols = Math.max(1, overlapColumnCount || 1);
  const col = Math.max(0, Math.min(overlapColumn || 0, cols - 1));
  const gapPx = 2;
  const widthPct = 100 / cols;
  const leftCalc = `calc(${col * widthPct}% + ${gapPx}px)`;
  const widthCalc = `calc(${widthPct}% - ${gapPx * 2}px)`;

  return (
    <div
      className={caja.className}
      data-tipo={caja["data-tipo"]}
      style={{
        position: "absolute",
        top: topPx,
        left: leftCalc,
        width: widthCalc,
        height: altoPx,
        zIndex: esActiva ? 25 : 5 + col,
        padding: 0,
        overflow: "hidden",
        boxSizing: "border-box",
        touchAction: "none",
        ...caja.style,
        ...(esActiva ? { boxShadow: "0 4px 14px rgba(0,0,0,0.35)", opacity: 0.95 } : {}),
      }}
    >
      <div
        role="button"
        tabIndex={-1}
        title="Arrastrar para cambiar día u hora"
        onPointerDown={onMoverStart}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: HANDLE_TOP,
          cursor: esActiva ? "grabbing" : "grab",
          zIndex: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 5px",
          boxSizing: "border-box",
          background: "transparent",
        }}
      >
        <GripBar color={COLOR_ASA_CITA} />
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            color: ink,
            letterSpacing: "0.02em",
            textTransform: "uppercase",
            lineHeight: 1,
            flexShrink: 0,
            maxWidth: "55%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {tipoLabel}
        </span>
      </div>

      <div
        onClick={(e) => {
          e.stopPropagation();
          if (huboMovimientoRef?.current) return;
          onAbrir?.(cita);
        }}
        title={tip}
        style={{
          position: "absolute",
          top: HANDLE_TOP,
          left: 0,
          right: 0,
          bottom: puedeResize ? HANDLE_BOTTOM : 0,
          padding: "2px 6px",
          cursor: "pointer",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: bloqueBajo ? "center" : "flex-start",
          gap: 1,
        }}
      >
        <div style={{ ...lineStyle, fontSize: 11, fontWeight: 600 }}>{linea1}</div>
        {linea2 ? (
          <div
            style={{
              ...lineStyle,
              fontSize: bloqueBajo ? 9 : 10,
              fontWeight: 500,
              lineHeight: 1.15,
            }}
          >
            {linea2}
          </div>
        ) : null}
      </div>

      {puedeResize && (
        <div
          role="button"
          tabIndex={-1}
          title="Arrastrar para cambiar la duración"
          onPointerDown={onResizeStart}
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: HANDLE_BOTTOM,
            cursor: "ns-resize",
            zIndex: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
          }}
        >
          <span style={{ fontSize: 9, color: COLOR_ASA_CITA, letterSpacing: 1 }}>⋯</span>
        </div>
      )}
    </div>
  );
}
